import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty, omitBy, isNil } from "lodash";
import { useHistory, useLocation } from "react-router-dom";
import { routeToTrolleyView } from "Helpers/index";
import moment from "moment";
import { DataTable } from "@life-line-lab/components-react";
import DataTableFilter from "Components/shared/data-table-filter";
import { Row } from "antd";

export interface Option {
  label: string;
  value: string;
}

interface SortingTrolleyListProps {
  onFilter?: any;
  customerOptions?: any;
  sortingHubOptions?: any;
  allOrderList?: any;
  //TODO: Modify these optional / required after getting the actual data
}

const SortingTrolleyList: React.FC<SortingTrolleyListProps> = ({
  onFilter,
  customerOptions,
  sortingHubOptions,
  allOrderList,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation()

  const initialFilter: any = {
    customerOrgList: "",
    sortingHub: "",
  };

  const [filterState, setFilterState] = useState<{ [key: string]: string | boolean }>(
    initialFilter,
  );

  useEffect(() => {
    if (onFilter && !isEmpty(filterState)) {
      const filterPayload = omitBy(filterState, (input) => isNil(input) || isEmpty(input));
      onFilter(filterPayload);
    }
  }, [filterState]);

  const handleBoxTrolleyPage = () => {
    history.push(`/fullscreen/tracking?type=1`);
  };

  const handleOpenPickupTrolleyView = (trolleyID: string) => {
    // not sure what this params is for
    const params = "redirect=sort"
    routeToTrolleyView(trolleyID, location, history, params)
  };

  const initialFilterSelection = [
    {
      key: "customerOrgList",
      name: "customerOrgList",
      label: t("placeholder.filterCustomer"),
      placeholder: t("placeholder.filterCustomer"),
      value: filterState?.customerOrgList,
      options: [{ label: t("option.all"), value: "" }].concat(
        customerOptions?.map((org) => {
          return {
            label: org.name || "",
            value: org._id,
          };
        }),
      ),
    },
    {
      key: "sortingHub",
      name: "sortingHub",
      label: t("placeholder.byOriginHub"),
      placeholder: t("placeholder.byOriginHub"),
      value: filterState?.sortingHub,
      options: [{ label: t("option.all"), value: "" }].concat(
        sortingHubOptions?.map((hub) => {
          return {
            label: hub.locationName || "",
            value: hub._id,
          };
        }),
      ),
    },
  ];

  const handleSearch = async (text: string) => {
    setFilterState((prevFilter) => ({ ...prevFilter, orFields: text }));
  };

  const handleFilterSelection = (selection) => {
    setFilterState((prevFilter) => ({
      ...prevFilter,
      customerOrgList: selection.customerOrgList,
      sortingHub: selection.sortingHub,
      deliveryType: selection.deliveryType,
      handoverPoint: selection.handoverPoint,
    }));
  };

  const columns: any = [
    {
      title: t("label.trolley").toLocaleUpperCase(),
      dataIndex: "trolleyCode",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <div className="align-flex-end">
            <span
              className="half-square custom-icon mb-5"
              style={{ borderLeft: `12px solid transparent`, borderBottom: `12px solid #E8A51A` }}
            >
              &nbsp;
            </span>
            <a className="clickable-id">&nbsp;{record.trolleyCode || ""}</a>
          </div>
        );
      },
      onCell: (record, rowIndex) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            handleOpenPickupTrolleyView(record._id);
          },
        };
      },
    },
    {
      title: t("label.originHub").toLocaleUpperCase(),
      dataIndex: ["sortingHubData", "name"],
      width: 150,
      ellipsis: true,
      render: (sortingHub: string) => {
        return (
          <span>{sortingHub || t("label.missingTrolleyRoute")}</span>
        );
      },
    },
    {
      title: t("label.batch").toLocaleUpperCase(),
      dataIndex: "batchCode",
      width: 150,
      ellipsis: true,
      sorter: (a, b) => a?.batchCode?.localeCompare(b?.batchCode),
    },
    {
      title: t("label.noOfBoxes").toLocaleUpperCase(),
      dataIndex: "boxParcelList",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record?.boxParcelList?.length}</span>;
      },
    },
    {
      title: t("label.status").toLocaleUpperCase(),
      dataIndex: "status",
      width: 100,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <span className={`status ${record.status}`}>
            {record?.status?.replaceAll("_", " ") || ""}
          </span>
        );
      },
    },
    {
      title: t("label.dateTime").toLocaleUpperCase(),
      dataIndex: "createdAt",
      ellipsis: true,
      render: (_: any, record: any) => {
        return record.createdAt ? (
          <span>
            {moment(record.createdAt).format("DD-MM-YYYY") +
              " | " +
              moment(record.createdAt).format("hh:mm a")}
          </span>
        ) : (
          <span>&#11835;</span>
        );
      },
    },
    {
      title: t("label.weightInKg").toLocaleUpperCase(),
      dataIndex: "totalWeight",
      ellipsis: true,
      render: (totalParcelWeight: string) => {
        const roundedWeight = parseFloat(totalParcelWeight).toFixed(2);
        return <span>{roundedWeight}</span>;
      },
    },
    {
      title: t("label.weightLoadFactor").toLocaleUpperCase(),
      dataIndex: "weightLoadFactor",
      ellipsis: true,
    },
    {
      title: t("label.volume").toLocaleUpperCase(),
      dataIndex: "totalVolume",
      ellipsis: true,
    },
    {
      title: t("label.volLoadFactor").toLocaleUpperCase(),
      dataIndex: "volumeLoadFactor",
      ellipsis: true,
    },
    {
      title: t("label.handoverPoint").toLocaleUpperCase(),
      dataIndex: ["handoverPointData", "name"],
      width: 105,
      ellipsis: true,
      render: (handoverPoint: string) => {
        return (
          <span>{handoverPoint || t("label.missingTrolleyRoute")}</span>
        );
      },
    },
  ];

  return (
    <>
      <div className="parcel-order-list">
        <DataTable
          rowKey={(record: any) => {
            return record._id;
          }}
          columns={columns}
          dataSource={allOrderList}
          scroll={{ x: true }}
          title={() => (
            <div>
              <div className="filter-component">
                <DataTableFilter
                  searchPlaceholder={t("placeholder.searchByTrolleyNo")}
                  onSearch={(text: string) => {
                    handleSearch(text);
                  }}
                  onFilter={(selection) => handleFilterSelection(selection)}
                  renderFilterSelection={initialFilterSelection}
                />
              </div>
              <Row className="flex-space-between">
                <div>
                  <h4>Total Trolley: {allOrderList?.length}</h4>
                </div>
                <div>
                  <h4>
                    <a onClick={handleBoxTrolleyPage}>{t("label.fullscreen")} </a>
                  </h4>
                </div>
              </Row>
            </div>
          )}
        />
      </div>
    </>
  );
};

export default SortingTrolleyList;
